"use client";

import { useState, useRef, useEffect, ReactNode } from "react";
import { Icon } from "@iconify/react";
import { cn } from "@/lib/utils";
import { uiIcons } from "@/config/icons";
import { usePathname } from "next/navigation";
import { getLocaleFromPathname } from "@/lib/i18n";
import { getDictionary } from "@/lib/messages";

interface FileUploaderProps {
  accept?: string;
  multiple?: boolean;
  onFilesSelected: (files: File[]) => void;
  className?: string;
  title?: string;
  description?: string;
  children?: ReactNode;
}

export function FileUploader({ 
  accept, 
  multiple = false, 
  onFilesSelected, 
  className,
  title,
  description,
  children
}: FileUploaderProps) {
  const pathname = usePathname() || "/";
  const locale = getLocaleFromPathname(pathname);
  const dictionary = getDictionary(locale);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const resolvedTitle = title ?? dictionary.uploader.title;
  const resolvedDescription = description ?? dictionary.uploader.description;

  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      // Check if we are focusing on an input or textarea, if so, don't intercept
      const activeElement = document.activeElement;
      if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA')) {
        return;
      }

      if (e.clipboardData && e.clipboardData.files.length > 0) {
        e.preventDefault();
        const files = Array.from(e.clipboardData.files);
        // Filter by accept if provided
        // This is a basic check, a more robust one might be needed for complex mime types
        const filteredFiles = accept 
          ? files.filter(file => {
              const types = accept.split(',').map(t => t.trim().toLowerCase());
              return types.some(type => {
                if (type.startsWith('.')) {
                  // Extension check
                  return file.name.toLowerCase().endsWith(type);
                } else if (type.endsWith('/*')) {
                  // Mime type group check (e.g. image/*)
                  const mainType = type.split('/')[0];
                  return file.type.startsWith(mainType);
                } else {
                  // Exact mime type
                  return file.type === type;
                }
              });
            })
          : files;

        if (filteredFiles.length > 0) {
          onFilesSelected(filteredFiles);
        }
      }
    };

    window.addEventListener("paste", handlePaste);
    return () => window.removeEventListener("paste", handlePaste);
  }, [accept, onFilesSelected]);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const files = Array.from(e.dataTransfer.files);
      onFilesSelected(files);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files);
      onFilesSelected(files);
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div
      className={cn(
        "relative flex flex-col items-center justify-center rounded-[45px] border-2 border-dashed border-positivus-dark/30 bg-positivus-grey px-6 py-16 text-center transition-all duration-300 hover:border-positivus-dark hover:bg-positivus-green/20 cursor-pointer group",
        isDragging && "border-positivus-dark bg-positivus-green/30 scale-[1.02]",
        className
      )}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={handleClick}
    >
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        accept={accept}
        multiple={multiple}
        onChange={handleFileInput}
      />
      
      {children ? (
        children
      ) : (
        <>
          <div className={cn(
            "mb-6 rounded-full bg-white border-2 border-positivus-dark p-6 transition-all duration-300 group-hover:scale-110 group-hover:bg-positivus-green",
            isDragging && "scale-110 bg-positivus-green"
          )}>
            <Icon icon={uiIcons.upload} className="h-10 w-10 text-positivus-dark" />
          </div>
          
          <h3 className="mb-2 text-2xl font-medium text-positivus-dark">
            {resolvedTitle}
          </h3>
          <p className="mb-6 text-base text-gray-600">
            {resolvedDescription}
          </p>
          
          <div className="text-sm font-medium bg-white border border-positivus-dark px-4 py-2 rounded-full inline-block">
            {accept
              ? `${dictionary.uploader.supports}: ${accept.split(',').join(', ')}`
              : dictionary.uploader.supportsAll}
          </div>
        </>
      )}
    </div>
  );
}
